import React from "react";
import { Student } from "@/types/Student";
import { Badge } from "@/components/ui";
import { useTranslation } from "react-i18next";
import { StudentImage } from "./StudentImage";
import {
  FunnelStateEnumToGroup,
  translateGroupName,
} from "@/util/funnelGroups";
import { getTopPercentileBadge, isActive } from "@/util/studentUtils";
import { cn } from "@/lib/utils";

export const StudentProfileImage = ({
  student,
  className,
  activeClassName,
}: {
  student: Partial<Student>;
  className?: string;
  activeClassName?: string;
}) => {
  const { studentOrganizationStatus } = student;
  const { lastActiveAt, cohortYear } = studentOrganizationStatus || {};
  const { t } = useTranslation();

  const percentileBadge = getTopPercentileBadge(
    t,
    studentOrganizationStatus?.commitPercentile || 100,
  );

  return (
    <div className="relative">
      <div
        className={cn("h-full w-full overflow-hidden rounded-md", className)}
      >
        <StudentImage student={student} />
      </div>
      {isActive(lastActiveAt) && (
        <span
          className={cn(
            "absolute -right-[10px] -bottom-[10px] h-[22px] w-[22px] rounded-full border-5 border-background bg-green-500",
            activeClassName,
          )}
          aria-label={t("aria.studentActive")}
          role="status"
          data-testid="student-active-indicator"
        />
      )}
      <div className="absolute bottom-2 left-2 flex gap-2.5">
        {studentOrganizationStatus?.funnelState
          ? FunnelStateEnumToGroup["STUDENT_PROFILE"][
              studentOrganizationStatus.funnelState
            ]?.map((group) => {
              return (
                <Badge
                  key={group}
                  variant="secondary"
                  className="rounded-full bg-foreground/60 px-2.5 py-0.5 text-xs font-bold tracking-wider text-primary-foreground uppercase backdrop-blur-lg"
                  data-testid="student-funnel-state-badge"
                >
                  {translateGroupName(group)} {cohortYear}
                </Badge>
              );
            })
          : null}
        {percentileBadge && (
          <Badge
            aria-label={percentileBadge}
            variant="translucent"
            className="rounded-full bg-white/70 px-2.5 py-0.5 text-xs font-bold tracking-wider backdrop-blur-lg"
          >
            {percentileBadge}
          </Badge>
        )}
      </div>
    </div>
  );
};
